import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { nuvraAuth } from "@/auth/nuvraAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { NuvraLogo } from "@/components/NuvraLogo";
import { Footer } from "@/components/Footer";
import { OAuthButtons } from "@/components/auth/OAuthButtons";
import { Separator } from "@/components/ui/separator";
import { useI18n } from "@/i18n/I18nContext";
import { supabase } from "@/integrations/supabase/client";
import { authRedirectUrl } from "@/shell/authRedirect";

export default function SignupPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get("next");
  const dest = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : "/dashboard";
  const { toast } = useToast();
  const { t } = useI18n();

  // Nuvra-owned signup. The server creates the identity; email confirmation is then requested through the configured Supabase Auth email provider.
  const resendVerification = async () => {
    setLoading(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: authRedirectUrl("/auth/callback") },
    });
    setLoading(false);
    if (error) {
      toast({ variant: "destructive", title: t("auth.signup.failedTitle"), description: "We could not resend the verification email. Please try again shortly." });
      return;
    }
    toast({ title: "Verification email sent", description: "Check your inbox and spam folder." });
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const result = await nuvraAuth.register({ email, password, displayName: name });
    setLoading(false);
    if (!result.success) {
      toast({ variant: "destructive", title: t("auth.signup.failedTitle"), description: result.error });
      return;
    }
    if (result.requiresEmailVerification) {
      setVerificationSent(true);
      const { error: resendError } = await supabase.auth.resend({
        type: "signup",
        email,
        options: { emailRedirectTo: authRedirectUrl("/auth/callback") },
      });
      if (resendError) {
        toast({ variant: "destructive", title: "Account created", description: "Your account is waiting for email verification. We could not send the email automatically; use resend below." });
      }
      return;
    }
    toast({ title: t("auth.signup.createdTitle"), description: t("auth.signup.createdBody") });
    navigate(dest);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-primary/10 blur-[120px]" />
      </div>

      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-md px-4 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="h-4 w-4" /> {t("auth.backToHome")}
          </Link>

          <Card className="border-border/50 bg-card/80 backdrop-blur-xl">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <NuvraLogo size={32} />
                <span className="font-bold text-xl text-foreground">Nuvra</span>
              </div>
              <CardTitle className="text-2xl">{t("auth.signup.title")}</CardTitle>
              <CardDescription>{t("auth.signup.subtitle")}</CardDescription>
            </CardHeader>
            {verificationSent ? (
              <>
                <CardContent className="space-y-5 text-center pb-8">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <MailCheck className="h-7 w-7" aria-hidden="true" />
                  </div>
                  <div className="space-y-2">
                    <CardTitle className="text-xl">Check your email</CardTitle>
                    <CardDescription>
                      We sent a verification link to <span className="font-medium text-foreground">{email}</span>. Confirm your email before signing in.
                    </CardDescription>
                  </div>
                  <div className="space-y-2">
                    <Button type="button" className="w-full" onClick={resendVerification} disabled={loading}>
                      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                      Resend verification email
                    </Button>
                    <Button type="button" variant="ghost" className="w-full" onClick={() => setVerificationSent(false)} disabled={loading}>
                      Use a different email
                    </Button>
                  </div>
                </CardContent>
                <CardFooter className="justify-center">
                  <p className="text-sm text-muted-foreground">Already verified? <Link to="/login" className="text-primary hover:underline">Sign in</Link></p>
                </CardFooter>
              </>
            ) : (
              <>
            <CardContent className="space-y-4">
              <OAuthButtons />
              <div className="relative">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground">{t("auth.or")}</span>
              </div>
            </CardContent>
            <form onSubmit={handleSignup}>
              <CardContent className="space-y-4 pt-0">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("auth.signup.fullName")}</Label>
                  <Input id="name" autoComplete="name" placeholder={t("auth.signup.namePlaceholder")} value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t("auth.email")}</Label>
                  <Input id="email" type="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t("auth.password")}</Label>
                  <Input id="password" type="password" autoComplete="new-password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
                </div>
              </CardContent>
              <CardFooter className="flex-col gap-4">
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t("auth.signup.createAccount")}
                </Button>
                <p className="text-sm text-muted-foreground">
                  {t("auth.signup.haveAccount")}{" "}
                  <Link to="/login" className="text-primary hover:underline">{t("auth.signup.loginLink")}</Link>
                </p>
              </CardFooter>
            </form>
              </>
            )}
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}
